import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { AlertTriangle, Check, Star, Building, Crown, Sparkles } from 'lucide-react'
import { useNavigate, Link } from 'react-router-dom'
import { usePlans } from '../../hooks/usePlans'
import { redirectToCheckout } from '../../services/stripeService'
import { useAuth } from '../../contexts/AuthContext'
import { customPlanPath } from '../../lib/customPlan'
import toast from 'react-hot-toast'

interface TrialExpiredModalProps {
  isOpen: boolean
}

/**
 * Modal bloqueante exibido quando o trial expira
 * Mostra os planos disponíveis com oferta especial
 * Não permite fechar sem escolher um plano
 */
const TrialExpiredModal: React.FC<TrialExpiredModalProps> = ({ isOpen }) => {
  const navigate = useNavigate()
  const { plans, isLoading } = usePlans({ isActive: true })
  const { isAdmin } = useAuth()
  const [loadingPriceId, setLoadingPriceId] = useState<string | null>(null)
  const [selectedDuration, setSelectedDuration] = useState<'monthly' | 'annual'>('monthly')

  const handleSubscribe = async (priceId: string) => {
    setLoadingPriceId(priceId)
    try {
      await redirectToCheckout(priceId)
    } catch (error) {
      toast.error('Erro ao processar pagamento. Tente novamente.')
      setLoadingPriceId(null)
    }
  }

  // Don't show for admins
  if (!isOpen || isAdmin) return null

  // Filter plans by duration and exclude free plans
  const filteredPlans = plans?.filter(plan => {
    const matchesDuration = selectedDuration === 'annual'
      ? plan.duration_months === 12
      : plan.duration_months === 1
    const isNotFree = !plan.name.toLowerCase().includes('gratuito') &&
                      !plan.name.toLowerCase().includes('free')
    return matchesDuration && isNotFree
  }) || []

  // Sort: Premium first, then Escolar
  const orderedPlans = filteredPlans.sort((a, b) => {
    if (a.name.includes('Premium')) return -1
    if (b.name.includes('Premium')) return 1
    return 0
  })

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-surface dark:bg-ink-700 rounded-caderno-md shadow-caderno-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blood-500 to-ochre-500 p-8 text-center text-white">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
            >
              <AlertTriangle className="w-16 h-16 mx-auto mb-4" />
            </motion.div>
            <h2 className="text-3xl font-bold mb-2">Seu Trial Expirou</h2>
            <p className="text-lg text-white/90">
              Escolha um plano para continuar aproveitando todos os recursos da plataforma
            </p>
          </div>

          {/* Duration Toggle */}
          <div className="flex justify-center py-6 bg-cream-50 dark:bg-ink-700/50">
            <div className="inline-flex items-center bg-surface dark:bg-ink-700 rounded-caderno-sm p-1 shadow-caderno-sm">
              <button
                onClick={() => setSelectedDuration('monthly')}
                className={`px-6 py-2 rounded-caderno-sm text-sm font-medium transition-all ${
                  selectedDuration === 'monthly'
                    ? 'bg-terra-500 text-white shadow-caderno-sm'
                    : 'text-ink-500 dark:text-ink-300 hover:text-ink-900 dark:hover:text-cream-50'
                }`}
              >
                Mensal
              </button>
              <button
                onClick={() => setSelectedDuration('annual')}
                className={`px-6 py-2 rounded-caderno-sm text-sm font-medium transition-all ${
                  selectedDuration === 'annual'
                    ? 'bg-terra-500 text-white shadow-caderno-sm'
                    : 'text-ink-500 dark:text-ink-300 hover:text-ink-900 dark:hover:text-cream-50'
                }`}
              >
                Anual
                <span className="ml-2 text-xs bg-moss-500 text-white px-2 py-0.5 rounded-full">
                  -25%
                </span>
              </button>
            </div>
          </div>

          {/* Plans */}
          <div className="p-8">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-terra-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {orderedPlans.map((plan, index) => {
                  const isPremium = plan.name.includes('Premium')
                  const isEscolar = plan.name.includes('Escolar')
                  const price = parseFloat(plan.price)
                  const monthlyPrice = selectedDuration === 'annual' ? price / 12 : price

                  return (
                    <motion.div
                      key={plan.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`relative rounded-caderno-md p-6 border-2 transition-all hover:shadow-lg ${
                        isPremium
                          ? 'border-terra-500 bg-terra-50 dark:bg-terra-500/20'
                          : 'border-terra-500 bg-terra-50 dark:bg-terra-500/20'
                      }`}
                    >
                      {/* Popular Badge */}
                      {isPremium && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <div className="bg-terra-500 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                            <Star className="w-4 h-4" />
                            Mais Popular
                          </div>
                        </div>
                      )}

                      {/* Icon */}
                      <div className={`w-12 h-12 rounded-caderno-sm flex items-center justify-center mb-4 ${
                        isPremium ? 'bg-terra-100 dark:bg-terra-500/30' : 'bg-terra-100 dark:bg-terra-500/30'
                      }`}>
                        {isPremium ? (
                          <Crown className={`w-6 h-6 ${isPremium ? 'text-terra-500' : 'text-terra-500'}`} />
                        ) : (
                          <Building className={`w-6 h-6 ${isEscolar ? 'text-terra-500' : 'text-terra-500'}`} />
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="text-2xl font-bold text-ink-900 dark:text-cream-50 mb-2">
                        {plan.name.replace(' Anual', '')}
                      </h3>

                      {/* Price */}
                      <div className="mb-6">
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold text-ink-900 dark:text-cream-50">
                            R$ {monthlyPrice.toFixed(2).replace('.', ',')}
                          </span>
                          <span className="text-ink-500 dark:text-ink-300">/mês</span>
                        </div>
                        {selectedDuration === 'annual' && (
                          <p className="text-sm text-moss-500 dark:text-moss-500 mt-1">
                            Cobrança anual de R$ {price.toFixed(2).replace('.', ',')}
                          </p>
                        )}
                      </div>

                      {/* Features */}
                      <ul className="space-y-3 mb-6">
                        {plan.features?.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Check className={`w-5 h-5 ${isPremium ? 'text-terra-500' : 'text-terra-500'} mt-0.5 flex-shrink-0`} />
                            <span className="text-sm text-ink-700 dark:text-cream-300">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA Button */}
                      <button
                        onClick={() => handleSubscribe(plan.stripe_price_id || '')}
                        disabled={loadingPriceId === plan.stripe_price_id}
                        className={`w-full py-3 px-4 rounded-caderno-sm font-semibold text-white transition-all ${
                          isPremium
                            ? 'bg-terra-500 hover:bg-terra-700 shadow-caderno-lg'
                            : 'bg-terra-500 hover:bg-terra-700 shadow-caderno-lg'
                        } ${loadingPriceId === plan.stripe_price_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {loadingPriceId === plan.stripe_price_id ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Processando...
                          </span>
                        ) : (
                          'Assinar Agora'
                        )}
                      </button>
                    </motion.div>
                  )
                })}
              </div>
            )}

            {/* Footer Info */}
            <div className="mt-8 text-center space-y-3">
              <div className="flex items-center justify-center gap-2 text-terra-500 dark:text-terra-300">
                <Sparkles className="w-5 h-5" />
                <p className="font-medium">Oferta Especial de Lançamento: 20% de desconto no primeiro mês!</p>
              </div>
              <p className="text-sm text-ink-500 dark:text-ink-300">
                Todos os planos incluem 7 dias de garantia • Cancele quando quiser
              </p>
              {/* Link sai de /app/* → UserDashboardWrapper desmonta e o modal some. Se TrialComponents um dia
                  subir para o AppShell, /planos/personalizado precisa entrar em TRIAL_MODAL_EXEMPT_PATHS. */}
              <p className="text-sm text-ink-500 dark:text-ink-300">
                Representa uma rede ou secretaria?{' '}
                <Link
                  to={customPlanPath('trial')}
                  className="font-medium text-terra-500 dark:text-terra-300 hover:underline"
                >
                  Conheça o plano personalizado →
                </Link>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default TrialExpiredModal
